import FormField from 'components/FormField';
import { FV } from './share';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormItemProps, Radio } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import { stores2storeScopes } from '../share';
import { useTranslate } from '@tolgee/react';
import { useItemSelectContext } from './ItemSelectProvider';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import useSmartField from 'hooks/useSmartField';
import { useHelpContext } from '../HelpContext';
import { InlineHelp } from '../Help';

export function useGetItemsLabel() {
  const { t } = useTranslate();

  return {
    t,
    getItemsLabel: useCallback(
      (activityType: Knowledge.Activity.Type) => {
        return activityType === 'RECOMMEND_ACTIVITY'
          ? t('recommend_item')
          : activityType === 'CLEARANCE_SALE_ACTIVITY' || activityType === 'LIVE_STREAMING_ACTIVITY'
          ? t('促销商品')
          : t('act_field_items');
      },
      [t],
    ),
  };
}

function Items({ itemsSelected, disabled }: { itemsSelected?: boolean } & DisabledProps) {
  const { t, getItemsLabel } = useGetItemsLabel();

  const { show } = useItemSelectContext();

  const {
    values: { activityType, stores },
  } = useFormikContext<FV>();

  const label = getItemsLabel(activityType);

  const {
    field: { value, onChange },
    meta: { touched, error },
  } = useSmartField<FV['items']>('items', (value): string | undefined =>
    (activityType === 'RECOMMEND_ACTIVITY' || !allItems || itemsSelected) &&
    itemsSelected !== false &&
    !value.length
      ? t('department.请选择x', { x: label })
      : undefined,
  );

  const storeScopes = useMemo(() => stores2storeScopes(stores), [stores]);

  const [selectItems, setSelectItems] = useState(
    () => value.map(item2selectItem) as unknown as ItemSelectV2.Item[],
  );

  const [allItems, setAllItems] = useState(value.length === 0);

  const refsCurrent = {
    value,
    onChange,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (stores.length === 0) {
      return;
    }

    const { value, onChange } = refs.current;

    const nextItems = value.filter((_) =>
      stores.some(({ storeId }) => `${storeId}` === `${_.storeId}`),
    );

    if (nextItems.length === value.length) {
      return;
    }

    onChange(nextItems);

    setSelectItems((items) =>
      items.filter(({ internalStoreId }) =>
        stores.some(({ storeId }) => `${storeId}` === `${internalStoreId}`),
      ),
    );
  }, [stores]);

  const { noItemStores, ...formItemProps } = useMemo<
    FormItemProps & { noItemStores?: Knowledge.Activity.Store[] }
  >(() => {
    if ((allItems && activityType !== 'RECOMMEND_ACTIVITY') || itemsSelected === false) {
      return {};
    }

    if (touched && error) {
      return {
        help: error,
        validateStatus: 'error',
      };
    }

    if (activityType === 'RECOMMEND_ACTIVITY' && (!value || !value.length)) {
      return {
        help: t('activity_recommend_item_tips'),
      };
    }

    return {};
  }, [error, itemsSelected, allItems, touched, value, activityType, t]);

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();

  return (
    <FormField<FV['items']> {...formItemProps} name="items" required label={label}>
      {({ field: { value, onChange, onBlur } }) => {
        if (itemsSelected === false) {
          return t('aide.全部商品');
        }

        const onShow = () => {
          show({
            storeType: 'ALL',
            storeScopes,
            skuDisabled: activityType === 'RECOMMEND_ACTIVITY',
            sideStoreEnable: true,
            defaultFoldSku: true,
            value: selectItems,
            initialStoreScopes: noItemStores ? stores2storeScopes(noItemStores) : undefined,
            onChange: (value) => {
              setSelectItems(value);
              onChange(value.map(selectItem2item));
            },
            onCancel: () => {
              setTimeout(() => onBlur());
            },
          });

          setHelpStep('适用商品');
        };

        const action = [
          value.length === 0 ? (
            <Button
              disabled={disabled}
              style={{ paddingLeft: 0 }}
              type="link"
              icon={<PlusOutlined />}
              onClick={onShow}
            >
              {t('活动机器人-选择商品')}
            </Button>
          ) : (
            <div>
              {t('selected_items', { count: value.length })}
              <Button disabled={disabled} type="link" icon={<EditOutlined />} onClick={onShow} />
            </div>
          ),
        ];

        if (itemsSelected === true || activityType === 'RECOMMEND_ACTIVITY') {
          return action;
        }

        const content = (
          <div className="inline-block">
            <div className="pt-[6px] pb-[6px]">
              <Radio
                disabled={disabled}
                checked={allItems}
                onChange={({ target: { checked } }) => {
                  setAllItems(checked);
                  setSelectItems([]);
                  setTimeout(() => onChange([]));
                  setHelpStep('适用商品');
                }}
              >
                {t('aide.全部商品')}
              </Radio>
              <Radio
                disabled={disabled}
                checked={!allItems}
                onChange={({ target: { checked } }) => {
                  setAllItems(!checked);
                  setTimeout(() => onChange([]));
                  setHelpStep('适用商品');
                }}
              >
                {t('aide.指定商品')}
              </Radio>
            </div>
            {allItems ? null : <div className="inline-block px-[8px] py-[4px]">{action}</div>}
          </div>
        );

        return <InlineHelp step="适用商品">{content}</InlineHelp>;
      }}
    </FormField>
  );
}

export function item2selectItem(item: Knowledge.Activity.Item): any {
  return {
    externalItemId: item.spuId,
    internalItemId: item.itemInternalId,
    skuInfo: item.skus.map(({ skuId }) => ({ skuId })),
  };
}

export function selectItem2item(item: ItemSelectV2.Item): Knowledge.Activity.Item {
  return {
    platform: item.platform,
    region: item.region,
    storeId: Number(item.internalStoreId),
    shopId: item.externalShopId,
    shopName: item.shopName,
    shopAlias: item.shopAlias,
    spuId: item.externalItemId,
    itemInternalId: Number(item.internalItemId),
    title: item.itemName,
    itemUrl: item.itemUrl,
    imageUrl: item.imageUrl?.[0],
    sellerSpu: item.itemSku,
    skus: item.skuInfo?.map((sku: any) => {
      return {
        skuId: sku.skuId,
        sellerSku: sku.sellerSku,
        skuName: sku.skuName,
      };
    }),
  };
}

export default Items;
